/**
 * TagPicker — inline multi-select тегов в стиле Linear/Notion.
 *
 * В свёрнутом виде показывает чипсы (Badge) выбранных тегов
 * (или placeholder если ничего не выбрано).
 *
 * По клику открывается Popover:
 *  - search-input сверху (локальный фильтр по name);
 *  - inline-список тегов из справочника crm_tags (is_active=true);
 *  - чекмарк на выбранных;
 *  - клик по строке тогглит выбор, popover НЕ закрывается (multi-select).
 *
 * `value` — массив имён тегов (string[]), как хранится в lead.tags / partner.tags.
 * При выборе сохраняем именно name, без id — никаких миграций.
 *
 * При canEdit=false — display только (Badge'и или плейсхолдер), без popover.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  type MantineSize,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { InlinePicker } from '@ayocore/platform/client/components/InlinePicker';
import { useTagRepository } from '@ayocore/erp/crm/client/rpc';
import type { Tag } from '../../../schemas/tag.schema';

export interface TagPickerProps {
  /** Текущий набор тегов (имена). */
  value: string[];
  /** Колбек с новым набором. */
  onChange: (tags: string[]) => void;
  /** Плейсхолдер когда пусто. По умолчанию "Не указаны". */
  placeholder?: string;
  /** Если false — display только, без popover. */
  canEdit?: boolean;
  /** Размер свёрнутого display. По умолчанию 'xs'. */
  size?: MantineSize;
  /** Размер содержимого popover. По умолчанию 'sm'. */
  inputSize?: MantineSize;
}

type TagOptionRowProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
  size: MantineSize;
};

function TagOptionRow({ label, selected, onClick, size }: TagOptionRowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: 4,
        background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <Group gap={8} wrap='nowrap' justify='space-between'>
        <Badge size={size} variant='light' color='green' style={{ textTransform: 'none' }}>
          {label}
        </Badge>
        {selected ? <IconCheck size={14} color='var(--mantine-color-blue-6)' /> : null}
      </Group>
    </Box>
  );
}

export function TagPicker({
  value,
  onChange,
  placeholder = 'Не указаны',
  canEdit = true,
  size = 'sm',
  inputSize = 'sm',
}: TagPickerProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const tagRepository = useTagRepository();

  // Lazy-загрузка справочника при первом открытии popover.
  useEffect(() => {
    if (!opened || tags.length > 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await tagRepository
          .where('is_active', true)
          .orderBy('name', 'asc')
          .limit(500)
          .fetch();
        if (!cancelled) setTags(rows ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, tags.length, tagRepository]);

  const filtered = useMemo(() => {
    if (!search.trim()) return tags;
    const q = search.trim().toLowerCase();
    return tags.filter(t => t.name.toLowerCase().includes(q));
  }, [tags, search]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggleTag = (name: string) => {
    const next = selectedSet.has(name) ? value.filter(t => t !== name) : [...value, name];
    onChange(next);
  };

  const display =
    value.length > 0 ? (
      <Group gap={4} wrap='wrap'>
        {value.map((tag, idx) => (
          <Badge
            key={`${tag}-${idx}`}
            size='sm'
            variant='light'
            color='green'
            style={{ textTransform: 'none', cursor: 'inherit' }}
          >
            {tag}
          </Badge>
        ))}
      </Group>
    ) : (
      <Text
        size={size}
        c='dimmed'
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {placeholder}
      </Text>
    );

  return (
    <InlinePicker
      display={display}
      opened={opened}
      onOpenChange={setOpened}
      canEdit={canEdit}
      width={300}
    >
      <Stack gap={6}>
        <TextInput
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder='Поиск тега...'
          size={inputSize}
          autoFocus
        />
        <ScrollArea.Autosize mah={300} type='hover' scrollbarSize={6} scrollbars='y'>
          <Stack gap={2}>
            {filtered.map(t => (
              <TagOptionRow
                key={t.id}
                label={t.name}
                selected={selectedSet.has(t.name)}
                onClick={() => toggleTag(t.name)}
                size={inputSize}
              />
            ))}
            {loading ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Загрузка...
              </Text>
            ) : null}
            {!loading && filtered.length === 0 ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                {search ? 'Ничего не найдено' : 'Нет доступных тегов'}
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </InlinePicker>
  );
}
